import { Box, Button, Flex, Text } from '@mantine/core';
import { Navigate, NavLink, useSearchParams } from 'react-router';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileUploadWithEntities } from '../hooks/file-uploads/useFileUploadWithEntities';
import {
  selectActionedDetectionCount,
  selectCanClearFileData,
  selectFinalizeStats,
  selectIsExpired,
  selectTotalDetectionCount,
  selectVisibleCategories,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import CategoryEntities from './CategoryEntities';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useDisclosure } from '@mantine/hooks';
import { FinalizeReviewModal } from './FinalizeReviewModal';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';

export default function DetectedEntitiesPage() {
  const { clientId } = useCurrentClient();
  const [searchParams] = useSearchParams();
  const params = {
    fileId: searchParams.get('fileId') ?? undefined,
    fileName: searchParams.get('fileName') ?? undefined,
    guid: searchParams.get('guid') ?? undefined,
    originMeetingId: searchParams.get('originMeetingId') ?? undefined,
  };
  const { setFileResolved, file, isLoading, error, refetch } = useFileUploadWithEntities();
  const actionedDetectionCount = useDetectedEntitiesStore(selectActionedDetectionCount);
  const totalDetectionCount = useDetectedEntitiesStore(selectTotalDetectionCount);
  const isExpired = useDetectedEntitiesStore(selectIsExpired);
  const canClear = useDetectedEntitiesStore(state => selectCanClearFileData(state, params?.guid));
  const clearFileData = useDetectedEntitiesStore(state => state.clearFileData);
  const clearExpiredFiles = useDetectedEntitiesStore(state => state.clearExpiredFiles);
  const setFileId = useDetectedEntitiesStore(state => state.setFileId);
  const visibleCategories = useDetectedEntitiesStore(selectVisibleCategories);
  const { showDataUploadCounts } = useFlags();
  const [finalizeModalVisible, { open: openFinalizeModal, close: closeFinalizeModal }] =
    useDisclosure(false);
  const counts = useDetectedEntitiesStore(selectFinalizeStats);
  const { originMeetingId } = params || {};
  useEffect(() => {
    if (canClear && params?.guid && params.fileId) {
      clearFileData(params.fileId, params.guid);
      if (!isLoading) refetch();
    }
  }, [isLoading, canClear]);

  useEffect(() => {
    if (params?.fileId) {
      setFileId(params?.fileId);
      clearExpiredFiles();
    }
  }, [params?.fileId]);

  async function handleResolveFile() {
    const total = counts.inserts + counts.updates + counts.unreviewed;
    Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_COMPLETE, {
      dataUploadFileId: params?.fileId,
      inserts: counts.inserts,
      updates: counts.updates,
      unreviewed: counts.unreviewed,
      percentageActioned: ((counts.inserts + counts.updates) / total) * 100,
      percentageIgnored: (counts.unreviewed / total) * 100,
    });
    return setFileResolved(true);
  }

  const backUrl = originMeetingId
    ? `/meetings/${originMeetingId}`
    : `/client/${clientId}/document/add`;

  if (!params?.fileId || (isExpired && !canClear)) {
    return <Navigate to={backUrl} />;
  }

  if (file?.isResolved && !finalizeModalVisible) {
    return <Navigate to={backUrl} />;
  }

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  return (
    <Box m="lg">
      <Flex direction="row" justify="space-between" align="center">
        <NavLink
          to={backUrl}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--mantine-spacing-xxs)',
            textDecoration: 'none',
          }}
          data-testid="back-button"
        >
          <IconArrowLeft style={{ cursor: 'pointer' }} />
          <Text c="gray.10" fw={500} size="md">
            Back to data overview
          </Text>
        </NavLink>

        <Button variant="primary" onClick={openFinalizeModal} leftSection={<IconCheck size={16} />}>
          Finalise review
        </Button>
      </Flex>

      <Text c="gray.10" fw={600} size={'xxl'} mt="xl">
        Review imported data{' '}
        {showDataUploadCounts && `(${actionedDetectionCount}/${totalDetectionCount} Done)`}
      </Text>

      <Flex direction="column">
        {visibleCategories.map((category, i) => (
          <CategoryEntities
            key={category}
            category={category}
            bottomBorder={i !== visibleCategories.length - 1}
          />
        ))}
      </Flex>

      <FinalizeReviewModal
        opened={finalizeModalVisible}
        close={closeFinalizeModal}
        newCount={counts.inserts}
        existingCount={counts.updates}
        unreviewedCount={counts.unreviewed}
        onConfirm={handleResolveFile}
        navigateTo={backUrl}
      />
    </Box>
  );
}
